import { getDownloadURL, ref as storageRef } from 'firebase/storage';
import { storage } from '../firebase/config';

type CacheEntry = {
  objectUrl: string;
  updatedAtKey: string;
};

const memoryCache = new Map<string, CacheEntry>();
const MAX_ENTRIES = 80;

const buildCacheKey = (path: string, updatedAt?: string) =>
  `${path}|${updatedAt || ''}`;

const revokeIfBlob = (url: string) => {
  if (url.startsWith('blob:')) URL.revokeObjectURL(url);
};

const evictIfNeeded = () => {
  if (memoryCache.size <= MAX_ENTRIES) return;
  const firstKey = memoryCache.keys().next().value;
  if (firstKey) {
    const entry = memoryCache.get(firstKey);
    if (entry?.objectUrl) revokeIfBlob(entry.objectUrl);
    memoryCache.delete(firstKey);
  }
};

/** Storage REST 경로만 추출 (과거 URL 저장 대비) */
const normalizeStoragePath = (pathOrUrl: string): string => {
  const raw = pathOrUrl.trim();
  if (!raw.startsWith('http://') && !raw.startsWith('https://')) return raw;
  try {
    const match = raw.match(/\/o\/([^?]+)/);
    if (match?.[1]) return decodeURIComponent(match[1]);
  } catch {
    /* ignore */
  }
  return raw;
};

export const assignmentSubmissionImageCache = {
  getCached(path: string, updatedAt?: string): string | null {
    if (!path) return null;
    const storagePath = normalizeStoragePath(path);
    return memoryCache.get(buildCacheKey(storagePath, updatedAt))?.objectUrl ?? null;
  },

  /** Callable 등으로 받은 URL을 캐시에 저장 (멤버 제출 이미지용) */
  remember(path: string, url: string, updatedAt?: string) {
    if (!path || !url) return;
    const storagePath = normalizeStoragePath(path);
    const key = buildCacheKey(storagePath, updatedAt);
    evictIfNeeded();
    memoryCache.set(key, { objectUrl: url, updatedAtKey: updatedAt || '' });
  },

  async getObjectUrl(path: string, updatedAt?: string): Promise<string | null> {
    if (!path) return null;
    const storagePath = normalizeStoragePath(path);
    const key = buildCacheKey(storagePath, updatedAt);
    const hit = memoryCache.get(key);
    if (hit) return hit.objectUrl;

    Array.from(memoryCache.entries()).forEach(([k, entry]) => {
      if (k.startsWith(`${storagePath}|`)) {
        revokeIfBlob(entry.objectUrl);
        memoryCache.delete(k);
      }
    });

    try {
      // getBytes는 버킷 CORS 설정 없으면 localhost에서 차단됨 → getDownloadURL + img src 사용
      const url = await getDownloadURL(storageRef(storage, storagePath));
      evictIfNeeded();
      memoryCache.set(key, { objectUrl: url, updatedAtKey: updatedAt || '' });
      return url;
    } catch (error) {
      console.warn('과제 제출 이미지 로드 실패:', storagePath, error);
      return null;
    }
  },

  revoke(path: string, updatedAt?: string) {
    const storagePath = normalizeStoragePath(path);
    const key = buildCacheKey(storagePath, updatedAt);
    const entry = memoryCache.get(key);
    if (entry?.objectUrl) revokeIfBlob(entry.objectUrl);
    memoryCache.delete(key);
  },

  clearAll() {
    Array.from(memoryCache.values()).forEach((entry) => {
      if (entry.objectUrl) revokeIfBlob(entry.objectUrl);
    });
    memoryCache.clear();
  }
};
